import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import store from './store';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import BlogIndex from './pages/BlogIndex';
import EnglishToSpanishSubtitleGenerator from './pages/EnglishToSpanishSubtitleGenerator';
import SeoBlogPage from './pages/SeoBlogPage';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import CopyrightPolicy from './pages/CopyrightPolicy';
import Edit from './components/navHelpers/Edit';
import TestRedux from './components/TestRedux';
import Error from './pages/Error';
import SchedulerApp from './pages/scheduler/SchedulerAppLayout';
import config from './config';

function App() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant',
        });
    }, [pathname]);

    const [isUserSignedIn, setIsUserSignedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [doctors, setDoctors] = useState(null);
    const [offices, setOffices] = useState(null);
    const [insurances, setInsurances] = useState(null);
    const [bodily_parts, setBodilyParts] = useState(null);
    const [originalDoctors, setOriginalDoctors] = useState(null);

    // Start true so Navbar does not open the auth modal before the first /logged_in check finishes.
    const [fetchingLoggedIn, setFetchingLoggedIn] = useState(true);

    useEffect(() => {
        if (!localStorage.getItem('csrfToken')) {
            fetch(`${config.API_BASE_URL}/api/v1/csrf_token`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            })
                .then((response) => response.json())
                .then((data) => {
                    const csrfToken = data.csrf_token;
                    localStorage.setItem('csrfToken', csrfToken);
                })
                .catch((error) => {
                    console.error('Error fetching CSRF token:', error);
                });
        }
    }, []);

    useEffect(() => {
        let isMounted = true;

        const clearSignedInState = () => {
            setIsUserSignedIn(false);
            setUser(null);
            setDoctors(null);
            setOffices(null);
            setOriginalDoctors(null);
            setInsurances(null);
            setBodilyParts(null);
        };

        const checkLoggedIn = async () => {
            setFetchingLoggedIn(true);

            try {
                const res = await fetch(`${config.API_BASE_URL}/api/v1/logged_in`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                });

                if (!isMounted) return;

                if (!res.ok) {
                    clearSignedInState();
                    return;
                }

                const response = await res.json();

                if (!isMounted) return;

                if (response.logged_in) {
                    setIsUserSignedIn(true);
                    setUser(response.user);
                    setDoctors(response.doctors);
                    setOffices(response.offices);
                    setOriginalDoctors(response.doctors);
                    setInsurances(response.insurances);
                    setBodilyParts(response.bodily_parts);
                } else {
                    clearSignedInState();
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error fetching logged-in status:', error);
                    clearSignedInState();
                }
            } finally {
                if (isMounted) {
                    setFetchingLoggedIn(false);
                }
            }
        };

        checkLoggedIn();

        return () => {
            isMounted = false;
        };
    }, []);

    const manualSetDoctors = (doctors) => {
        setDoctors(doctors);
    };

    const handleLogout = () => {
        const csrfToken = localStorage.getItem('csrfToken');

        fetch(`${config.API_BASE_URL}/api/v1/logout`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            credentials: 'include',
        })
            .then((res) => {
                if (res.ok) {
                    setIsUserSignedIn(false);
                    setUser(null);
                    setDoctors(null);
                    setOffices(null);
                    setOriginalDoctors(null);
                    setInsurances(null);
                    setBodilyParts(null);
                }
            })
            .catch((error) => {
                console.error('Error logging out:', error);
            });
    };

    const resetDoctors = () => {
        setDoctors(originalDoctors);
    };

    return (
        <Provider store={store}>
            <>
                <Navbar
                    isUserSignedIn={isUserSignedIn}
                    fetchingLoggedIn={fetchingLoggedIn}
                    handleLogout={handleLogout}
                    user={user}
                    setIsUserSignedIn={setIsUserSignedIn}
                    setUser={setUser}
                    doctors={doctors}
                    setDoctors={setDoctors}
                    insurances={insurances}
                    setInsurances={setInsurances}
                    bodily_parts={bodily_parts}
                    setBodilyParts={setBodilyParts}
                    originalDoctors={originalDoctors}
                    setOriginalDoctors={setOriginalDoctors}
                />

                <Routes>
                    <Route
                        index
                        element={
                            <Home
                                isUserSignedIn={isUserSignedIn}
                                handleLogout={handleLogout}
                                user={user}
                                setIsUserSignedIn={setIsUserSignedIn}
                                setUser={setUser}
                                doctors={doctors}
                                setDoctors={setDoctors}
                                insurances={insurances}
                                setInsurances={setInsurances}
                                bodily_parts={bodily_parts}
                                setBodilyParts={setBodilyParts}
                                originalDoctors={originalDoctors}
                                setOriginalDoctors={setOriginalDoctors}
                            />
                        }
                    />

                    <Route
                        path="english-to-spanish-subtitle-generator"
                        element={<EnglishToSpanishSubtitleGenerator />}
                    />

                    <Route path="blog" element={<BlogIndex />} />

                    <Route
                        path="how-to-add-spanish-subtitles-to-video"
                        element={<SeoBlogPage />}
                    />

                    <Route
                        path="translate-youtube-shorts-to-spanish-subtitles"
                        element={<SeoBlogPage />}
                    />

                    <Route
                        path="add-spanish-captions-to-tiktok-instagram-reels"
                        element={<SeoBlogPage />}
                    />

                    <Route
                        path="why-spanish-subtitles-help-videos-reach-more-viewers"
                        element={<SeoBlogPage />}
                    />

                    <Route
                        path="schedulerApp"
                        element={
                            <SchedulerApp
                                manualSetDoctors={manualSetDoctors}
                                user={user}
                                doctors={doctors}
                                setDoctors={setDoctors}
                                insurances={insurances}
                                setInsurances={setInsurances}
                                bodily_parts={bodily_parts}
                                setBodilyParts={setBodilyParts}
                                resetDoctors={resetDoctors}
                                offices={offices}
                                setOriginalDoctors={setOriginalDoctors}
                                setOffices={setOffices}
                            />
                        }
                    />

                    <Route
                        path="edit"
                        element={
                            <Edit
                                manualSetDoctors={manualSetDoctors}
                                user={user}
                                doctors={doctors}
                                setDoctors={setDoctors}
                                insurances={insurances}
                                setInsurances={setInsurances}
                                bodily_parts={bodily_parts}
                                setBodilyParts={setBodilyParts}
                                resetDoctors={resetDoctors}
                                offices={offices}
                                setOriginalDoctors={setOriginalDoctors}
                                setOffices={setOffices}
                            />
                        }
                    />

                    <Route path="testRedux" element={<TestRedux />} />

                    <Route path="terms" element={<Terms />} />

                    <Route path="privacy" element={<Privacy />} />

                    <Route path="copyright" element={<CopyrightPolicy />} />

                    <Route path="*" element={<Error />} />
                </Routes>
            </>
        </Provider>
    );
}

export default App;